import { useCallback, useEffect, useRef } from 'react';

type TimerId = ReturnType<typeof setTimeout>;

/**
 * 컴포넌트 lifecycle에 묶인 setTimeout 래퍼.
 * - unmount 시 등록된 모든 타이머를 자동으로 clear
 * - 반환된 id는 일반 clearTimeout으로도 수동 해제 가능
 *
 * 사용 예:
 *   const setSafeTimeout = useSafeTimeout();
 *   useEffect(() => {
 *     setSafeTimeout(() => doSomething(), 100);
 *   }, [setSafeTimeout]);
 */
export const useSafeTimeout = () => {
  const timersRef = useRef<Set<TimerId>>(new Set());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(id => clearTimeout(id));
      timers.clear();
    };
  }, []);

  return useCallback((callback: () => void, delay: number): TimerId => {
    // eslint-disable-next-line no-restricted-globals -- useSafeTimeout 자체 구현
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      callback();
    }, delay);
    timersRef.current.add(id);
    return id;
  }, []);
};
